import { useCallback, useEffect, useReducer, useRef } from 'react'
import {
  reportingDraftInitialState,
  reportingDraftReducer,
} from 'src/agent/reporting/conversation/reportingDraft.reducer'
import { streamReportingRunEvents } from 'src/queries/reportingAgent/reportingAgent.sse'

export interface UseReportingDraftOptions {
  draftRequestId?: string
  onRequestIdChange?: (draftRequestId: string) => void
}

const makeRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    return crypto.randomUUID()
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export const useReportingDraft = ({
  draftRequestId,
  onRequestIdChange,
}: UseReportingDraftOptions) => {
  const [state, dispatch] = useReducer(
    reportingDraftReducer,
    reportingDraftInitialState,
  )
  const requestIdRef = useRef(draftRequestId ?? makeRequestId())
  const notifiedRequestIdRef = useRef(false)
  const streamAbortRef = useRef<AbortController | null>(null)
  const lastSequenceRef = useRef(0)

  useEffect(() => {
    if (draftRequestId && requestIdRef.current !== draftRequestId) {
      requestIdRef.current = draftRequestId
      notifiedRequestIdRef.current = true
    }
    dispatch({ type: 'entryInitialized', draftRequestId: requestIdRef.current })
    if (!draftRequestId && !notifiedRequestIdRef.current) {
      notifiedRequestIdRef.current = true
      onRequestIdChange?.(requestIdRef.current)
    }
  }, [draftRequestId, onRequestIdChange])

  useEffect(() => {
    lastSequenceRef.current = state.stream.lastSequence
  }, [state.stream.lastSequence])

  const startStream = useCallback(() => {
    streamAbortRef.current?.abort()
    streamAbortRef.current = new AbortController()
    dispatch({
      type: 'streamReconnected',
      afterSequence: lastSequenceRef.current,
    })
    return streamAbortRef.current.signal
  }, [])

  const stopStream = useCallback(() => {
    streamAbortRef.current?.abort()
    streamAbortRef.current = null
  }, [])

  const resetPrivateState = useCallback(() => {
    stopStream()
    requestIdRef.current = makeRequestId()
    notifiedRequestIdRef.current = false
    lastSequenceRef.current = 0
    dispatch({ type: 'authorizationLost' })
  }, [stopStream])

  const getStreamSequence = useCallback(() => lastSequenceRef.current, [])

  return {
    state,
    startStream,
    stopStream,
    getStreamSequence,
    resetPrivateState,
    stream: streamReportingRunEvents,
    dispatch,
  }
}
